import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc'; // ✅ 导入 utc 插件
import React from 'react';

import {
  AppBrand,
  AvatarDropdown,
  AvatarName,
  Footer,
  Question,
  SelectLang,
} from '@/components';
import {
  queryAppVersion,
  currentUser as queryCurrentUser,
} from '@/services/ant-design-pro/api';
import { getApiBaseUrl, getClientVersion } from '@/utils/runtimeConfig';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';

const loginPath = '/user/login';

const isPublicRoute = (path: string) =>
  path === loginPath ||
  path === '/authenticate' ||
  /^\/oidc\/interaction\/[^/]+$/.test(path) ||
  /^\/enroll\/auth\/[^/]+$/.test(path) ||
  path === '/mdm/byod/auth' ||
  /^\/enroll\/byod(?:\/[^/]+)?$/.test(path) ||
  path === '/user/otp-binding';

dayjs.extend(utc);

type AppVersions = {
  clientVersion: string;
  serverVersion: string;
};

type InitialState = {
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
  versions?: AppVersions;
};

const getDefaultVersions = (): AppVersions => ({
  clientVersion: getClientVersion(),
  serverVersion: '-',
});

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<InitialState> {
  const fetchUserInfo = async () => {
    try {
      const msg = await queryCurrentUser({
        skipErrorHandler: true,
      });
      const payload = msg.data as
        | API.CurrentUser
        | { user?: API.CurrentUser }
        | undefined;
      const user =
        payload && 'user' in payload
          ? payload.user
          : (payload as API.CurrentUser | undefined);
      return user ? { ...user, name: user.userName || user.name } : undefined;
    } catch (_error) {
      localStorage.removeItem('token');
      history.push(loginPath);
    }
    return undefined;
  };

  const fetchVersions = async (): Promise<AppVersions> => {
    const clientVersion = getClientVersion();

    try {
      const msg = await queryAppVersion({
        skipErrorHandler: true,
      });
      const payload = msg.data as API.VersionData | undefined;

      return {
        clientVersion,
        serverVersion: payload?.version || '-',
      };
    } catch (_error) {
      return {
        clientVersion,
        serverVersion: '-',
      };
    }
  };
  // 如果不是登录页面，执行
  const { location } = history;

  const currentPath = location.pathname;
  if (!isPublicRoute(currentPath)) {
    const currentUser = await fetchUserInfo();
    const versions = currentUser ? await fetchVersions() : getDefaultVersions();
    return {
      fetchUserInfo,
      currentUser,
      versions,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    versions: getDefaultVersions(),
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState }) => {
  return {
    actionsRender: () => [
      <Question key="doc" />,
      <SelectLang key="SelectLang" />,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      if (isPublicRoute(location.pathname)) {
        return;
      }
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser) {
        history.push(loginPath);
      }
      // 如果已登录但未绑定 OTP，且不在 OTP 绑定页面，则跳转到 OTP 绑定页面
      if (
        initialState?.currentUser &&
        !initialState.currentUser.otpEnabled &&
        location.pathname !== '/user/otp-binding'
      ) {
        history.push('/user/otp-binding');
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    headerTitleRender: (logoDom, titleDom) => (
      <AppBrand
        logo={logoDom}
        title={titleDom}
        clientVersion={initialState?.versions?.clientVersion}
        serverVersion={initialState?.versions?.serverVersion}
      />
    ),
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return <>{children}</>;
    },
    ...initialState?.settings,
  };
};

// ✅ 智能 baseURL 配置
const getBaseURL = (): string => {
  const runtimeBaseUrl = getApiBaseUrl();
  const envBaseUrl = process.env.REACT_APP_API_BASE_URL;

  if (process.env.NODE_ENV === 'production') {
    return runtimeBaseUrl || envBaseUrl || '';
  }

  // In local dev, prefer relative requests so Umi proxy rules can handle /api and /mdm.
  // Ignore the compile-time env base URL here because the repo's default .env points to a remote host.
  return runtimeBaseUrl || '';
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  baseURL: getBaseURL(),
  ...errorConfig,
};
